// src/pages/Facturas.jsx

import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  getDocs as getDocsQuery
} from 'firebase/firestore';

// Para exportar PDF
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const Facturas = () => {
  // --------------------------------
  // ESTADOS PARA PROVEEDORES
  // --------------------------------
  const [proveedores, setProveedores] = useState([]);
  const [nuevoProveedor, setNuevoProveedor] = useState('');
  const [rif, setRif] = useState('');
  const [tipoProveedor, setTipoProveedor] = useState('natural');
  const [porcentajeRetencion, setPorcentajeRetencion] = useState(75);

  // --------------------------------
  // ESTADOS PARA FACTURAS
  // --------------------------------
  const [facturas, setFacturas] = useState([]);
  const [proveedorSeleccionado, setProveedorSeleccionado] = useState(null);

  // Campos de factura
  const [montoUSD, setMontoUSD] = useState('');
  const [montoBS, setMontoBS] = useState('');
  const [fechaTope, setFechaTope] = useState('');

  // Filtros
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');

  // Tasa BCV (puedes ajustarla o traerla de contexto)
  const [tasaBCV, setTasaBCV] = useState(78);

  // --------------------------------
  // REFERENCIAS A FIRESTORE
  // --------------------------------
  const proveedoresRef = collection(db, 'proveedores');
  const facturasRef = collection(db, 'facturas');

  // --------------------------------
  // useEffect: CARGA INICIAL
  // --------------------------------
  useEffect(() => {
    const cargarDatos = async () => {
      const provSnap = await getDocs(proveedoresRef);
      const facSnap = await getDocs(facturasRef);

      const provs = provSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const facs = facSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Ordenar proveedores según # de facturas pendientes
      setProveedores(
        provs.sort((a, b) => {
          const pendientesA = facs.filter(f => f.proveedorId === a.id && f.estado === 'pendiente').length;
          const pendientesB = facs.filter(f => f.proveedorId === b.id && f.estado === 'pendiente').length;
          return pendientesB - pendientesA;
        })
      );

      setFacturas(facs);
    };
    cargarDatos();
  }, []);

  // --------------------------------
  // CREAR PROVEEDOR
  // --------------------------------
  const crearProveedor = async () => {
    if (!nuevoProveedor) return;

    await addDoc(proveedoresRef, {
      nombre: nuevoProveedor,
      rif,
      tipo: tipoProveedor,
      porcentajeRetencion: tipoProveedor === 'jurídico' ? porcentajeRetencion : 0
    });

    // Limpiar
    setNuevoProveedor('');
    setRif('');
    setTipoProveedor('natural');
    setPorcentajeRetencion(75);

    // Recargar
    const provSnap = await getDocs(proveedoresRef);
    const facSnap = await getDocs(facturasRef);
    const provs = provSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    const facs = facSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    setProveedores(
      provs.sort((a, b) => {
        const pendientesA = facs.filter(f => f.proveedorId === a.id && f.estado === 'pendiente').length;
        const pendientesB = facs.filter(f => f.proveedorId === b.id && f.estado === 'pendiente').length;
        return pendientesB - pendientesA;
      })
    );
  };

  // --------------------------------
  // ELIMINAR PROVEEDOR
  // --------------------------------
  const eliminarProveedor = async (id) => {
    const confirmDelete = window.confirm('¿Seguro deseas eliminar este proveedor?');
    if (confirmDelete) {
      await deleteDoc(doc(db, 'proveedores', id));
      const provSnap = await getDocs(proveedoresRef);
      setProveedores(provSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setProveedorSeleccionado(null);
    }
  };

  // =====================================
  // CREAR FACTURA (registrarFactura)
  // =====================================
  const registrarFactura = async () => {
    if (!proveedorSeleccionado || (!montoUSD && !montoBS)) return;

    // subTotalConIVA (ya con IVA)
    const subTotalConIVA = montoUSD
      ? parseFloat(montoUSD)
      : parseFloat(montoBS) / tasaBCV;

    // neto
    const neto = subTotalConIVA / 1.16;

    // IVA Completo
    const IVACompleto = subTotalConIVA - neto;

    // retención si es jurídico
    let retencionIVA = 0;
    let montoFinal = subTotalConIVA;

    if (proveedorSeleccionado.tipo === 'jurídico' && proveedorSeleccionado.porcentajeRetencion) {
      const factor = proveedorSeleccionado.porcentajeRetencion / 100; // e.g. 1.0 => 100%, 0.75 => 75%
      retencionIVA = IVACompleto * factor;
      montoFinal = subTotalConIVA - retencionIVA;
    }

    // convertir a BS si no lo ingresó
    const bsCalculado = montoBS
      ? parseFloat(montoBS)
      : parseFloat(montoUSD) * tasaBCV;

    // (A) Creamos la FACTURA en firestore
    const facturaDocRef = await addDoc(facturasRef, {
      proveedorId: proveedorSeleccionado.id,
      monto: subTotalConIVA,     // con IVA
      montoBS: bsCalculado,
      estado: 'pendiente',
      fecha: new Date().toISOString(),
      fechaTope,
      montoRetenido: retencionIVA,
      montoFinal
    });

    // (B) Si retención > 0, creamos doc en 'retenciones'
    if (retencionIVA > 0 && proveedorSeleccionado.tipo === 'jurídico') {
      await addDoc(collection(db, 'retenciones'), {
        tipo: 'factura',
        proveedor: proveedorSeleccionado.nombre,
        rif: proveedorSeleccionado.rif || '',
        monto: subTotalConIVA,
        retencion: retencionIVA,
        total: montoFinal,
        fecha: new Date(),      // objeto Date para toLocaleDateString
        facturaId: facturaDocRef.id
      });
    }

    // Limpiar
    setMontoUSD('');
    setMontoBS('');
    setFechaTope('');

    // Recargar facturas
    const facSnap = await getDocs(facturasRef);
    setFacturas(facSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  // =====================================
  // MARCAR COMO PAGADA
  // =====================================
  const marcarComoPagada = async (idFactura) => {
    await updateDoc(doc(db, 'facturas', idFactura), {
      estado: 'pagada',
      fechaPagada: new Date().toISOString()
    });

    // (Opcional) Podrías actualizar la retención en 'retenciones' para indicar algo, p.e. 'estado: pagada'
    // Buscamos la retención con retenciones.facturaId === idFactura
    const q = query(collection(db, 'retenciones'), where('facturaId', '==', idFactura));
    const snap = await getDocsQuery(q);
    snap.forEach(async (retDoc) => {
      // si quieres, indica retención saldada o algo
      // e.g.:
      // await updateDoc(doc(db, 'retenciones', retDoc.id), { estado: 'pagada' });
    });

    // recargar
    const facSnap = await getDocs(facturasRef);
    setFacturas(facSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  // =====================================
  // ABONAR FACTURA
  // =====================================
  const abonarFactura = async (idFactura, montoActual, montoBSActual) => {
    const abono = prompt('Monto a abonar (USD):');
    const abonoFloat = parseFloat(abono);

    if (!isNaN(abonoFloat) && abonoFloat > 0) {
      const nuevoMonto = montoActual - abonoFloat;
      const nuevoMontoBS = montoBSActual - abonoFloat * tasaBCV;

      if (nuevoMonto <= 0) {
        // Salda la factura
        await updateDoc(doc(db, 'facturas', idFactura), {
          estado: 'pagada',
          monto: 0,
          montoBS: 0,
          fechaPagada: new Date().toISOString()
        });
      } else {
        // Actualiza montos
        await updateDoc(doc(db, 'facturas', idFactura), {
          monto: nuevoMonto,
          montoBS: nuevoMontoBS
        });
      }

      // (Opcional) Actualizar doc en 'retenciones' según el nuevo monto, si deseas
      // e.g. si cambia la base, retención se recalcula => tu lógica contable

      // recargar facturas
      const facSnap = await getDocs(facturasRef);
      setFacturas(facSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }
  };

  // =====================================
  // ELIMINAR FACTURA
  // =====================================
  const eliminarFactura = async (idFactura) => {
    const confirmDelete = window.confirm('¿Estás seguro de eliminar esta factura?');
    if (confirmDelete) {
      // (1) Eliminar la factura
      await deleteDoc(doc(db, 'facturas', idFactura));

      // (2) Buscar en retenciones, si existe retenciones con .facturaId = idFactura
      const q = query(collection(db, 'retenciones'), where('facturaId', '==', idFactura));
      const snap = await getDocsQuery(q);
      snap.forEach(async (retDoc) => {
        // eliminar doc en retenciones
        await deleteDoc(doc(db, 'retenciones', retDoc.id));
      });

      // recargar facturas
      const facSnap = await getDocs(facturasRef);
      setFacturas(facSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }
  };

  // ========================
  // FILTROS
  // ========================
  const facturasPorProveedor = (id) => facturas.filter(f => f.proveedorId === id);

  const pendientes = (id) => facturasPorProveedor(id).filter(f => f.estado === 'pendiente');

  const pagadas = (id) => {
    let arr = facturasPorProveedor(id).filter(f => f.estado === 'pagada');
    if (fechaInicio) arr = arr.filter(fac => new Date(fac.fecha) >= new Date(fechaInicio));
    if (fechaFin) arr = arr.filter(fac => new Date(fac.fecha) <= new Date(fechaFin));
    return arr;
  };

  // Total pendiente USD
  const totalPendienteUSD = facturas
    .filter(f => f.estado === 'pendiente')
    .reduce((acc, curr) => acc + parseFloat(curr.monto), 0);

  // ========================
  // EXPORTAR FACTURAS PAGADAS PDF
  // ========================
  const exportarFacturasPDF = () => {
    if (!proveedorSeleccionado) return;

    const docPdf = new jsPDF();
    docPdf.setFontSize(16);
    docPdf.text('Reporte de Facturas Pagadas', 14, 15);

    const rows = pagadas(proveedorSeleccionado.id).map((f) => [
      proveedorSeleccionado?.nombre || '',
      proveedorSeleccionado?.rif || '',
      `$${f.monto?.toFixed(2)}`,
      `$${f.montoRetenido?.toFixed(2) || '0.00'}`,
      `$${f.montoFinal?.toFixed(2) || f.monto?.toFixed(2)}`,
      f.estado,
      f.fechaTope ? new Date(f.fechaTope).toLocaleDateString() : 'N/A',
      f.fechaPagada ? new Date(f.fechaPagada).toLocaleDateString() : '---'
    ]);

    docPdf.autoTable({
      head: [['Proveedor', 'RIF', 'Monto', 'Retenido', 'Monto Final', 'Estado', 'Fecha Tope', 'Fecha Pagada']],
      body: rows,
      startY: 25
    });

    docPdf.save(`facturas_${proveedorSeleccionado?.nombre || 'reporte'}.pdf`);
  };

  // ========================
  // RENDER
  // ========================
  return (
    <div className="facturas-container">
      <h2>Gestión de Facturas</h2>

      <div style={{ textAlign: 'right', marginBottom: '10px' }}>
        <strong>Total pendiente: ${totalPendienteUSD.toFixed(2)}</strong>
      </div>

      {/* CREAR PROVEEDOR */}
      <div className="proveedor-section">
        <input
          type="text"
          placeholder="Nombre del proveedor"
          value={nuevoProveedor}
          onChange={(e) => setNuevoProveedor(e.target.value)}
        />
        <input
          type="text"
          placeholder="RIF del proveedor"
          value={rif}
          onChange={(e) => setRif(e.target.value)}
        />
        <select value={tipoProveedor} onChange={(e) => setTipoProveedor(e.target.value)}>
          <option value="natural">Natural</option>
          <option value="jurídico">Jurídico</option>
        </select>

        {tipoProveedor === 'jurídico' && (
          <select
            value={porcentajeRetencion}
            onChange={(e) => setPorcentajeRetencion(parseInt(e.target.value))}
          >
            <option value={75}>75% Retención IVA</option>
            <option value={100}>100% Retención IVA</option>
          </select>
        )}

        <button onClick={crearProveedor}>Agregar proveedor</button>
      </div>

      {/* LISTA DE PROVEEDORES */}
      <div className="lista-proveedores">
        {proveedores.map((prov) => (
          <div
            key={prov.id}
            className={`proveedor-card ${proveedorSeleccionado?.id === prov.id ? 'activo' : ''}`}
            onClick={() => setProveedorSeleccionado(prov)}
          >
            {/* Nombre */}
            {prov.nombre}
            <br />
            {/* RIF y %retencion si es jurídico */}
            {prov.tipo === 'jurídico' && (
              <small style={{ color: '#aaa' }}>
                RIF: {prov.rif} • Retención: {prov.porcentajeRetencion || 0}%
              </small>
            )}

            {/* Aviso de pendiente */}
            {pendientes(prov.id).length > 0 && (
              <div style={{ color: 'red', fontSize: '12px' }}>
                Factura pendiente
              </div>
            )}
          </div>
        ))}
      </div>

      {/* DETALLE PROVEEDOR SELECCIONADO */}
      {proveedorSeleccionado && (
        <>
          <h3>Facturas de {proveedorSeleccionado.nombre}</h3>
          <p>
            <strong>RIF:</strong> {proveedorSeleccionado?.rif || 'No definido'}
            {proveedorSeleccionado?.tipo === 'jurídico' && (
              <span style={{ marginLeft: '8px', color: 'orange' }}>
                (Retención: {proveedorSeleccionado?.porcentajeRetencion || 0}%)
              </span>
            )}
          </p>

          <button
            style={{ background: '#c00', color: 'white' }}
            onClick={() => eliminarProveedor(proveedorSeleccionado.id)}
          >
            Eliminar proveedor
          </button>

          {/* FORM PARA FACTURAS */}
          <div className="factura-form">
            <input
              type="number"
              placeholder="Monto en USD (c/IVA)"
              value={montoUSD}
              onChange={(e) => setMontoUSD(e.target.value)}
            />
            <input
              type="number"
              placeholder="Monto en BS (c/IVA)"
              value={montoBS}
              onChange={(e) => setMontoBS(e.target.value)}
            />
            <label className="fecha-label">Fecha tope de pago</label>
            <input
              type="date"
              value={fechaTope}
              onChange={(e) => setFechaTope(e.target.value)}
            />

            <button onClick={registrarFactura}>Registrar factura</button>
            <button onClick={exportarFacturasPDF}>Exportar a PDF</button>
          </div>

          {/* FACTURAS PENDIENTES */}
          <h4>Facturas Pendientes</h4>
          <ul className="facturas-lista">
            {pendientes(proveedorSeleccionado.id).map((f) => (
              <li key={f.id} className="factura-item">
                <p>
                  <strong>Monto (c/IVA):</strong> ${f.monto.toFixed(2)} |{' '}
                  {f.montoBS?.toFixed(2)} Bs
                </p>
                {proveedorSeleccionado.tipo === 'jurídico' && (
                  <>
                    <p><strong>Retenido:</strong> ${f.montoRetenido?.toFixed(2)}</p>
                    <p><strong>Monto Final:</strong> ${f.montoFinal?.toFixed(2)}</p>
                  </>
                )}
                <p><strong>Estado:</strong> {f.estado}</p>
                <p><strong>Fecha:</strong> {new Date(f.fecha).toLocaleString()}</p>
                <p><strong>Fecha tope:</strong> {f.fechaTope || 'No asignada'}</p>
                <div style={{ marginTop: '8px' }}>
                  <button onClick={() => marcarComoPagada(f.id)}>Marcar como pagada</button>
                  <button onClick={() => abonarFactura(f.id, f.monto, f.montoBS)}>Abonar</button>
                  <button onClick={() => eliminarFactura(f.id)}>Eliminar</button>
                </div>
              </li>
            ))}
          </ul>

          {/* FACTURAS PAGADAS */}
          <h4>Facturas Pagadas</h4>
          {pagadas(proveedorSeleccionado.id).length === 0 && (
            <p>No hay facturas pagadas para este proveedor.</p>
          )}
          <ul className="facturas-lista">
            {pagadas(proveedorSeleccionado.id).map((f) => (
              <li key={f.id} className="factura-item">
                <p>
                  <strong>Monto (c/IVA):</strong> ${f.monto.toFixed(2)} |{' '}
                  {f.montoBS?.toFixed(2)} Bs
                </p>
                <p><strong>Retenido:</strong> ${f.montoRetenido?.toFixed(2)}</p>
                <p><strong>Monto Final:</strong> ${f.montoFinal?.toFixed(2)}</p>
                <p><strong>Estado:</strong> {f.estado}</p>
                <p>
                  <strong>Fecha pagada:</strong>{' '}
                  {f.fechaPagada ? new Date(f.fechaPagada).toLocaleString() : '---'}
                </p>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export default Facturas;
